import { type PaginationParams } from '@webapp/core/providers/Pagination/pagination.types'
import { buildQueryKey } from '@webapp/core/utils/react-query/buildQueryKey'
import { userRepository } from '@webapp/platform/iam/data-access/repositories/UserRepository'

// Preserve the legacy `userKeys` prefixes verbatim — these are the
// cache-invalidation contract (ADR-5). Do not adopt the `['iam', ...]` base
// used by `roleQueryConfig`; that would silently break invalidation.
const base = ['user']

export const userQueryConfig = {
  // Query: ['user', 'list', isActive, params, queryParams]
  // Invalidation prefix (no args): ['user', 'list']
  //
  // `isActive` is an optional leading segment, so `buildQueryKey` truncation
  // can't be used for the full key — it would drop `params`/`queryParams`
  // whenever `isActive` is undefined (e.g. the user list with no tab filter),
  // collapsing every search/page to the same cache entry. Mutations invalidate
  // by calling `getList()` with no args, relying on the `['user', 'list']`
  // prefix; the query hook passes all three args and needs the full key.
  getList: (
    isActive?: boolean,
    params?: PaginationParams,
    queryParams?: {}
  ) => ({
    queryKey:
      params === undefined
        ? buildQueryKey([...base, 'list'])
        : [...base, 'list', isActive, params, queryParams],
    queryFn: () => userRepository.list(isActive, { ...params, ...queryParams }),
  }),

  // ['user', 'list', isActive, 'unfiltered-count']
  // Plain positional key (no `buildQueryKey`): the trailing 'unfiltered-count'
  // segment must be preserved even when `isActive` is undefined.
  getUnfilteredCount: (isActive?: boolean) => ({
    queryKey: [...base, 'list', isActive, 'unfiltered-count'],
    queryFn: async () => {
      const result = await userRepository.list(isActive, {
        page: 1,
        pageSize: 1,
      })
      return result.pagination.count
    },
  }),

  // ['user', 'details', userId]
  getDetails: (userId: number | uuid) => ({
    queryKey: buildQueryKey([...base, 'details', userId]),
    queryFn: () => userRepository.getDetails(userId),
  }),

  // ['user', 'purchase-price-limit', userId]
  getPurchasePriceLimit: (userId: number) => ({
    queryKey: buildQueryKey([...base, 'purchase-price-limit', userId]),
    queryFn: () => userRepository.getPurchasePriceLimit(userId),
  }),

  // ['user', 'counts']
  getCounts: () => ({
    queryKey: buildQueryKey([...base, 'counts']),
    queryFn: () => userRepository.getCounts(),
  }),
}
